import { FormikErrors, FormikTouched } from 'formik';
import mapValuesDeep from 'map-values-deep';
import { v4 as uuidv4 } from 'uuid';

import { UNKNOWN_OPTION_VALUE } from '~/components/Autocomplete';
import { GetDraftCompanyResponse } from '~/services/rtkQuery/company/types';
import { CreateCompanyFormValues, CreateUserFormValues } from '~/types/formValues';
import { CompanyValidationApiErrors, UserValidationApiErrors } from '~/types/models';
import { calculateAcquired } from '~/utils/calculations';
import { getApiError } from '~/utils/helpers';

export const transformDraftCompanyForCreate = (
  draftCompany: GetDraftCompanyResponse['result']['data'],
): CreateCompanyFormValues => {
  return {
    form: {
      name: draftCompany.name,
      formerName: draftCompany.former_name || '',
      web: draftCompany.website_url,
      pitchbookUrl: draftCompany.pitchbook_url,
      foundedYear: draftCompany.year_founded ? draftCompany.year_founded.toString() : '',
      industries: draftCompany.industries.map(industry => ({
        value: industry.id,
        label: industry.name,
      })),
      firstFinancingDate: draftCompany.first_financing_date
        ? new Date(draftCompany.first_financing_date)
        : null,
      firstFinancingDealType: draftCompany.first_financing_deal_type
        ? {
            value: draftCompany.first_financing_deal_type.id,
            label: draftCompany.first_financing_deal_type.name,
          }
        : null,
      bio: draftCompany.bio || '',
      competitors: draftCompany.competitors
        ? draftCompany.competitors.map(competitor => ({
            value: competitor.external_id,
            label: competitor.name,
            meta: competitor.pb_id ? { pbId: competitor.pb_id } : undefined,
          }))
        : [{ label: 'Competitors unknown', value: UNKNOWN_OPTION_VALUE }],
      comments: draftCompany.comment || '',
      rounds: draftCompany.rounds.map(round => ({
        name: round.type,
        subrounds: round.sub_rounds.map(subround => ({
          id: uuidv4(),
          dealDate: subround.deal_date ? new Date(subround.deal_date) : null,
          announcedDate: subround.is_announced
            ? subround.announced_date
              ? new Date(subround.announced_date)
              : null
            : null,
          dealSize: subround.deal_size || '',
          securityType: subround.security_type
            ? { value: subround.security_type.id, label: subround.security_type.name }
            : null,
          cap: subround.cap ? subround.cap.toString() : '',
          discount: subround.discount ? subround.discount.toString() : '',
          interest: subround.interest ? subround.interest.toString() : '',
          conversionAmount: subround.conversion_amount ? subround.conversion_amount.toString() : '',
          preMoneyValuation:
            subround.pre_money_valuation || subround.effective_pre_money_valuation || '',
          postMoneyValuation:
            subround.post_money_valuation || subround.effective_post_money_valuation || '',
          acquiredPercent:
            subround.deal_size &&
            (subround.post_money_valuation || subround.effective_post_money_valuation)
              ? (
                  calculateAcquired(
                    subround.deal_size,
                    subround.post_money_valuation || subround.effective_post_money_valuation || '',
                  ) * 100
                ).toString()
              : '',
          acquiredCalc: subround.acquired_calc || '',
          employees: subround.employees ? subround.employees.toString() : '',
          ceo: {
            name: subround.ceo_name || '',
            education: subround.ceo_education
              ? { value: subround.ceo_education.id, label: subround.ceo_education.name }
              : null,
            biography: subround.ceo_biography || '',
            educationHistory: subround.ceo_education_history || '',
          },
          ceoIsFounder: subround.ceo_is_founder,
          ceoChanged: subround.ceo_changed,
          generatingRevenue: subround.generating_revenue,
          participatingPreferred: subround.participating_preferred,
          noAnnouncement: !subround.is_announced,
          revenueStatusChanged: subround.revenue_status_changed,
          generatingProfit: subround.generating_profit,
          investors: subround.investors.map(investor => ({
            id: investor.external_id,
            pbId: investor.pb_id || null,
            name: investor.name,
            lead: investor.lead,
            new: investor.new,
            corporate: investor.corporate,
          })),
        })),
      })),
    },
  };
};

export const transformCompanyValidationErrors = (
  error: unknown,
): FormikErrors<CreateCompanyFormValues> | null => {
  const apiError = getApiError(error);

  if (
    apiError &&
    'result' in apiError.data &&
    typeof apiError.data.result === 'object' &&
    apiError.data.result !== null &&
    Object.keys(apiError.data.result).length > 0
  ) {
    const { company: apiErrors } = apiError.data.result as CompanyValidationApiErrors;
    const roundsErrors: FormikErrors<CreateCompanyFormValues['form']['rounds']> | undefined =
      apiErrors.rounds ? [] : undefined;

    if (apiErrors.rounds && roundsErrors) {
      Object.entries(apiErrors.rounds).forEach(([name, round]) => {
        const roundErrors: FormikErrors<CreateCompanyFormValues['form']['rounds'][number]> = {
          subrounds: [],
        };

        Object.entries(round).forEach(([subroundName, subround]) => {
          if (Array.isArray(roundErrors.subrounds) && subroundName !== 'summary') {
            const subroundIndex = +subroundName.slice(1) - 1;

            roundErrors.subrounds[subroundIndex] = subround;
          }
        });

        const roundIndex = name.charCodeAt(0) - 65;

        roundsErrors[roundIndex] = roundErrors;
      });
    }

    return {
      form: {
        name: apiErrors.companyName,
        formerName: apiErrors.formerName,
        web: apiErrors.web,
        foundedYear: apiErrors.foundedYear,
        industries: apiErrors.industries,
        firstFinancingDate: apiErrors.firstFinancingDate,
        firstFinancingDealType: apiErrors.firstFinancingDealType,
        bio: apiErrors.bio,
        competitors: apiErrors.competitors,
        comments: apiErrors.comments,
        // @ts-expect-error type is correct
        rounds: roundsErrors,
      },
    };
  }

  return null;
};

export const transformUserValidationErrors = (
  error: unknown,
): FormikErrors<CreateUserFormValues> | null => {
  const apiError = getApiError(error);

  if (
    apiError &&
    'result' in apiError.data &&
    typeof apiError.data.result === 'object' &&
    apiError.data.result !== null &&
    Object.keys(apiError.data.result).length > 0
  ) {
    const apiErrors = apiError.data.result as UserValidationApiErrors;

    return {
      firstName: apiErrors.first_name,
      lastName: apiErrors.last_name,
      email: apiErrors.email,
      position: apiErrors.position,
      role: apiErrors.role,
      password: apiErrors.password,
    };
  }

  return null;
};

export const transformFormikErrorsForTouched = <FormikValues>(
  errors: FormikErrors<FormikValues>,
) => {
  return mapValuesDeep(errors, () => true) as FormikTouched<FormikValues>;
};
